export const SVG_NS = 'http://www.w3.org/2000/svg';

export type SvgAttrs = Record<string, string | number | undefined>;

/** Creates an SVG element with attributes and children (strings become text nodes). */
export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: SvgAttrs = {},
  children: Array<Node | string> = [],
): SVGElementTagNameMap[K] {
  const element = document.createElementNS(SVG_NS, tag);
  Object.entries(attrs).forEach(([name, value]) => {
    if (value !== undefined) element.setAttribute(name, String(value));
  });
  children.forEach(child => {
    element.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
  });
  return element;
}

let counter = 0;

/** Unique id for gradients/clip paths so several charts can coexist on a page. */
export function uid(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter.toString(36)}`;
}

/** Path `d` for a polyline through the points (2-decimal precision). */
export function linePath(points: ReadonlyArray<{ x: number; y: number }>): string {
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x.toFixed(2)},${point.y.toFixed(2)}`)
    .join(' ');
}

/** Closed area between an upper polyline and a lower polyline (lower is traversed in reverse). */
export function areaPath(
  upper: ReadonlyArray<{ x: number; y: number }>,
  lower: ReadonlyArray<{ x: number; y: number }>,
): string {
  if (!upper.length || !lower.length) return '';
  const top = linePath(upper);
  const bottom = [...lower]
    .reverse()
    .map(point => `L${point.x.toFixed(2)},${point.y.toFixed(2)}`)
    .join(' ');
  return `${top} ${bottom} Z`;
}
